import { useEffect, useRef, useState } from 'react'

import { searchOrganizationUsers } from '../../utilities/api'
import { useOrderActions } from '../../utilities/hooks/useOrderActions'

import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'

function userLabel(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  return name || user?.email || 'User'
}

export default function UsersSearch({ organization }) {
  const { openViewProfile } = useOrderActions()
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState([])
  const [open, setOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState(null)
  const requestId = useRef(0)
  const rootRef = useRef(null)

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  useEffect(() => {
    if (selected) return undefined

    const term = query.trim()
    if (term.length < 2) {
      requestId.current += 1
      setUsers([])
      setSearching(false)
      setOpen(false)
      return undefined
    }

    const current = ++requestId.current
    setSearching(true)
    setOpen(true)
    const timer = setTimeout(async () => {
      try {
        const res = await searchOrganizationUsers(organization?.uuid, term)
        if (current !== requestId.current) return
        setUsers(res?.data?.users || [])
      } catch (err) {
        if (current !== requestId.current) return
        console.error(err)
        setUsers([])
      } finally {
        if (current === requestId.current) setSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [query, organization?.uuid, selected])

  const chooseUser = (user) => {
    requestId.current += 1
    const email = String(user?.email || '').trim()
    setSelected(user)
    setQuery(userLabel(user))
    setOpen(false)
    setUsers([])
    openViewProfile({
      email,
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      organizationUUID: organization?.uuid,
      users_permissions_user: {
        id: user.id,
        email,
        firstName: user.firstName,
        lastName: user.lastName,
      },
    })
  }

  const onQueryChange = (event) => {
    setQuery(event.target.value)
    if (selected) setSelected(null)
  }

  return (
    <Stack gap={4}>
      <div
        ref={rootRef}
        className="position-relative"
        style={{ maxWidth: 480 }}
      >
        <Form.Label htmlFor="user-search">Find a user</Form.Label>
        <Form.Control
          id="user-search"
          className="bg-white"
          type="search"
          autoComplete="off"
          placeholder="Search by name or email"
          value={query}
          onChange={onQueryChange}
          onFocus={() => {
            if (!selected && query.trim().length >= 2) setOpen(true)
          }}
          aria-expanded={open}
          aria-controls="user-search-results"
          aria-autocomplete="list"
        />
        {open && (
          <div
            id="user-search-results"
            role="listbox"
            className="dropdown-menu show w-100 shadow"
            style={{ maxHeight: 280, overflowY: 'auto' }}
          >
            {searching && (
              <div className="dropdown-item-text text-muted">Searching…</div>
            )}
            {!searching && users.length === 0 && (
              <div className="dropdown-item-text text-muted">
                No users match that search
              </div>
            )}
            {users.map((user) => (
              <button
                key={user.id}
                type="button"
                role="option"
                aria-selected={false}
                className="dropdown-item"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseUser(user)}
              >
                <span className="d-block">{userLabel(user)}</span>
                {user.email && userLabel(user) !== user.email && (
                  <span className="d-block small text-muted">{user.email}</span>
                )}
                {!String(user.email || '').trim() && (
                  <span className="d-block small text-muted">No email</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </Stack>
  )
}
